// User-friendly message helper to replace technical errors with readable messages
export type MessageType = 'success' | 'destructive' | 'warning' | 'info' | 'loading' | 'default'

interface UserMessage {
  title: string
  description: string
  variant: MessageType
}

export const userMessages = {
  // Success messages
  success: {
    applicationCreated: {
      title: "Application Added",
      description: "The application has been successfully added to your inventory",
      variant: "success" as MessageType
    },
    applicationUpdated: {
      title: "Application Updated",
      description: "Your application details have been saved successfully",
      variant: "success" as MessageType
    },
    applicationDeleted: {
      title: "Application Removed",
      description: "The application has been removed from your inventory",
      variant: "success" as MessageType
    },
    licenseCreated: {
      title: "License Added",
      description: "The license has been successfully added",
      variant: "success" as MessageType
    },
    licenseUpdated: {
      title: "License Updated",
      description: "Your license details have been saved successfully",
      variant: "success" as MessageType
    },
    licenseDeleted: {
      title: "License Removed",
      description: "The license has been removed successfully",
      variant: "success" as MessageType
    },
    saveSuccess: {
      title: "Changes Saved",
      description: "Your changes have been saved successfully",
      variant: "success" as MessageType
    }
  },

  // Error messages
  error: {
    generic: {
      title: "Something went wrong",
      description: "We encountered an issue. Please try again in a moment",
      variant: "destructive" as MessageType
    },
    networkError: {
      title: "Connection Issue",
      description: "Please check your internet connection and try again",
      variant: "destructive" as MessageType
    },
    validationError: {
      title: "Please check your input",
      description: "Some required fields are missing or contain invalid information",
      variant: "destructive" as MessageType
    },
    permissionError: {
      title: "Access Denied",
      description: "You don't have permission to perform this action",
      variant: "destructive" as MessageType
    },
    saveError: {
      title: "Unable to save",
      description: "We couldn't save your changes. Please try again",
      variant: "destructive" as MessageType
    },
    deleteError: {
      title: "Unable to delete",
      description: "We couldn't remove this item. Please try again",
      variant: "destructive" as MessageType
    },
    loadError: {
      title: "Unable to load data",
      description: "We're having trouble loading this information. Please refresh the page",
      variant: "destructive" as MessageType
    }
  },

  // Warning messages
  warning: {
    unsavedChanges: {
      title: "Unsaved Changes",
      description: "You have unsaved changes that will be lost if you continue",
      variant: "warning" as MessageType
    },
    dataLoss: {
      title: "This action cannot be undone",
      description: "Please confirm that you want to permanently delete this item",
      variant: "warning" as MessageType
    }
  },

  // Info messages
  info: {
    loading: {
      title: "Loading...",
      description: "Please wait while we process your request",
      variant: "loading" as MessageType
    },
    noData: {
      title: "No data found",
      description: "There's nothing to display at the moment",
      variant: "info" as MessageType
    }
  }
}

// Helper function to get user-friendly error message
export function getUserFriendlyError(error: any): UserMessage {
  // If it's already a user message, return it
  if (error?.title && error?.description && error?.variant) {
    return error
  }

  // Check for specific error types
  if (error?.message) {
    const message = error.message.toLowerCase()
    
    // Handle account already exists error
    if (message.includes('account with this email already exists') || 
        message.includes('email already exists') ||
        message.includes('user already exists') ||
        message.includes('email address already registered') ||
        message.includes('already registered')) {
      return {
        title: "Account Already Exists",
        description: "An account with this email address already exists. Try signing in instead.",
        variant: "destructive" as MessageType
      }
    }

    // Handle Google account password reset error
    if (message.includes('password reset is not available for google accounts')) {
      return {
        title: "Cannot Reset Password",
        description: "Your account uses Google Sign-In, so you can't reset your password here. Please sign in using the 'Continue with Google' button instead.",
        variant: "destructive" as MessageType
      }
    }

    // Handle verification link errors specifically
    if (message.includes('verification')) {
      if (message.includes('expired')) {
        return {
          title: "Link Expired",
          description: "Your verification link has expired (15 minutes). Please request a new one.",
          variant: "destructive" as MessageType
        }
      }
      if (message.includes('already been used')) {
        return {
          title: "Link Already Used",
          description: "This verification link has already been used. Each link can only be used once.",
          variant: "destructive" as MessageType
        }
      }
      if (message.includes('invalid')) {
        return {
          title: "Invalid Link",
          description: "This verification link is invalid. Please request a new one.",
          variant: "destructive" as MessageType
        }
      }
    }
    
    if (message.includes('network') || message.includes('fetch')) {
      return userMessages.error.networkError
    }
    
    if (message.includes('validation') || message.includes('required') || message.includes('invalid')) {
      return userMessages.error.validationError
    }
    
    if (message.includes('permission') || message.includes('unauthorized') || message.includes('forbidden')) {
      return userMessages.error.permissionError
    }
  }

  // Check for HTTP status codes
  if (error?.status || error?.response?.status) {
    const status = error.status || error.response?.status
    
    switch (status) {
      case 400:
      case 422:
        return userMessages.error.validationError
      case 401:
      case 403:
        return userMessages.error.permissionError
      case 404:
        return {
          title: "Not Found",
          description: "The item you're looking for doesn't exist or has been removed",
          variant: "destructive" as MessageType
        }
      case 500:
      case 502:
      case 503:
        return {
          title: "Server Issue",
          description: "Our servers are experiencing issues. Please try again later",
          variant: "destructive" as MessageType
        }
    }
  }

  // Default to generic error, but prefer the server's own message when it has one —
  // otherwise specific-but-unmatched errors (e.g. email delivery failures) get silently
  // replaced with a useless "something went wrong" that gives the user no way to tell
  // a transient issue from something they need to fix.
  if (error?.message) {
    return {
      title: userMessages.error.generic.title,
      description: error.message,
      variant: "destructive" as MessageType
    }
  }

  return userMessages.error.generic
}

// Helper function for success messages
export function getSuccessMessage(action: string, item?: string): UserMessage {
  const actionKey = action.toLowerCase()
  const itemType = item?.toLowerCase() || 'item'
  
  switch (actionKey) {
    case 'create':
    case 'add':
      return {
        title: "Successfully Added",
        description: `The ${itemType} has been added successfully`,
        variant: "success" as MessageType
      }
    case 'update':
    case 'edit':
      return {
        title: "Successfully Updated", 
        description: `The ${itemType} has been updated successfully`,
        variant: "success" as MessageType
      }
    case 'delete':
    case 'remove':
      return {
        title: "Successfully Removed",
        description: `The ${itemType} has been removed successfully`,
        variant: "success" as MessageType
      }
    default:
      return userMessages.success.saveSuccess
  }
}